import React, { useState, useEffect } from 'react';
import '../index.css';
import './editTable2.css'; // oder deine bevorzugte CSS-Datei
import {
  searchRecordInAirTable,
  updateRecordInAirTable,
  createRecordInAirTable
} from '../functions.js/airTableDB';

const ALLOWED_TEXT_REGEX = /^[A-Za-z0-9ÄÖÜäöüß ._:\/-]*$/;

const TasksTablePage = () => {
  const [tasks, setTasks] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newTask, setNewTask] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      // Aufgaben laden
      const fetchedTasks = await searchRecordInAirTable('tasks');

      // Sortiere alphabetisch Gruppen nach groups[x].fields.f_name
      if (fetchedTasks) {
        fetchedTasks.sort((a, b) => a.fields.f_name.localeCompare(b.fields.f_name));
      }

      setTasks(fetchedTasks || []);

      // Rollen laden
      const fetchedRoles = await searchRecordInAirTable('roles');
      setRoles(fetchedRoles || []);

      setIsLoading(false);
    };

    fetchData();
  }, []);

  if (isLoading) {
    return <p>Lade Daten...</p>;
  }

  return (
    <div className="tasks-container">
      <h2>Deine Aufgaben-Vorlagen</h2>
      <TaskTable 
        tasks={tasks}
        setTasks={setTasks}
        roles={roles}
        newTask={newTask}
        setNewTask={setNewTask}
      />
    </div>
  );
};

const TaskTable = ({ tasks, setTasks, roles, newTask, setNewTask }) => {
  const [editedTasks, setEditedTasks] = useState({});
  const [originalTasks, setOriginalTasks] = useState({});

  // Neue Validierungsfunktionen
  const isValidName = (name) => {
    if (!name || name.trim() === '') return false;
    return ALLOWED_TEXT_REGEX.test(name);
  };

  const isValidClickupId = (id) => {
    if (!id || id.trim() === '') return false;
    return ALLOWED_TEXT_REGEX.test(id);
  };

  useEffect(() => {
    const edits = {};
    const originals = {};
    tasks.forEach((task) => {
      edits[task.id] = {
        name: task.fields?.name || '',
        clickup_tmpid: task.fields?.clickup_tmpid || '',
        // Falls nicht vorhanden, standardmäßig 0
        duration_days: task.fields?.duration_days || 0,
        role:
          task.fields?.role && task.fields.role.length > 0
            ? task.fields.role[0]
            : '',
      };
      originals[task.id] = { ...edits[task.id] };
    });
    setEditedTasks(edits);
    setOriginalTasks(originals);
  }, [tasks]);

  const handleInputChange = (taskId, field, value) => {
    // Validierung basierend auf dem Feld
    if (field === 'name' && value !== '' && !ALLOWED_TEXT_REGEX.test(value)) {
      return;
    }
    if (field === 'clickup_tmpid' && value !== '' && !ALLOWED_TEXT_REGEX.test(value)) {
      return;
    }

    setEditedTasks((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]:
          field === 'duration_days'
            ? parseInt(value, 10) || 0
            : value,
      },
    }));
  };

  const hasChanges = (taskId) => {
    const original = originalTasks[taskId] || {};
    const current = editedTasks[taskId] || {};
    return (
      (original.name !== current.name && isValidName(current.name)) ||
      (original.clickup_tmpid !== current.clickup_tmpid && isValidClickupId(current.clickup_tmpid)) ||
      original.duration_days !== current.duration_days ||
      original.role !== current.role
    );
  };

  const validateTaskFields = (task) => {
    return (
      isValidName(task.name) &&
      isValidClickupId(task.clickup_tmpid) &&
      task.duration_days > 0 &&
      task.role.trim() !== ''
    );
  };

  const handleSave = async (taskId) => {
    if (!hasChanges(taskId)) return;

    const current = editedTasks[taskId];
    if (!validateTaskFields(current)) {
      alert(
        'Bitte alle Felder (Name, Clickup TMP ID, Tage Dauer und Rolle) ausfüllen.'
      );
      return;
    }

    const updatedData = {
      name: current.name,
      clickup_tmpid: current.clickup_tmpid,
      duration_days: current.duration_days,
      role: current.role ? [current.role] : [],
    };

    await updateRecordInAirTable(taskId, updatedData, 'tasks');

    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? { ...task, fields: { ...task.fields, ...updatedData } }
          : task
      )
    );

    setOriginalTasks((prev) => ({
      ...prev,
      [taskId]: { ...current },
    }));
    alert('Aufgabe erfolgreich gespeichert!');
  };

  const handleDiscard = (taskId) => {
    setEditedTasks((prev) => ({
      ...prev,
      [taskId]: { ...originalTasks[taskId] },
    }));
  };

  const handleNewSave = async () => {
    if (!newTask || !validateTaskFields(newTask)) {
      alert(
        'Bitte alle Felder (Name, Clickup TMP ID, Tage Dauer und Rolle) ausfüllen.'
      );
      return;
    }

    const newData = {
      name: newTask.name,
      clickup_tmpid: newTask.clickup_tmpid,
      duration_days: newTask.duration_days,
      role: newTask.role ? [newTask.role] : [],
    };

    const result = await createRecordInAirTable(newData, 'tasks');
    if (result) {
      setTasks((prev) => [...prev, result]);
      setNewTask(null);
      alert('Neue Aufgabe erfolgreich erstellt!');
    }
  };

  const handleNewCancel = () => {
    setNewTask(null);
  };

  return (
    <>
      <table className="modern-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Clickup TMP ID</th>
            <th>Tage Dauer</th>
            <th>Rolle</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.id}>
              <td>
                <input
                  type="text"
                  value={editedTasks[task.id]?.name || ''}
                  onChange={(e) =>
                    handleInputChange(task.id, 'name', e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={editedTasks[task.id]?.clickup_tmpid || ''}
                  onChange={(e) =>
                    handleInputChange(task.id, 'clickup_tmpid', e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  value={editedTasks[task.id]?.duration_days || 0}
                  onChange={(e) =>
                    handleInputChange(task.id, 'duration_days', e.target.value)
                  }
                />
              </td>
              <td>
                <select
                  value={editedTasks[task.id]?.role || ''}
                  onChange={(e) =>
                    handleInputChange(task.id, 'role', e.target.value)
                  }
                >
                  <option value="">Wähle eine Rolle</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.fields?.name || role.id}
                    </option>
                  ))}
                </select>
              </td>
              <td className="actions-td">
                {hasChanges(task.id) && (
                  <>
                    <button
                      className="btn save"
                      onClick={() => handleSave(task.id)}
                    >
                      💾
                    </button>
                    <button
                      className="btn discard"
                      onClick={() => handleDiscard(task.id)}
                    >
                      ✖
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}

          {/* Neue Aufgabe (wenn aktiv) */}
          {newTask && (
            <tr className="new-row">
              <td>
                <input
                  type="text"
                  placeholder="Name"
                  value={newTask.name}
                  onChange={(e) =>
                    setNewTask({ ...newTask, name: e.target.value })
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Clickup TMP ID"
                  value={newTask.clickup_tmpid}
                  onChange={(e) =>
                    setNewTask({ ...newTask, clickup_tmpid: e.target.value })
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  placeholder="Tage Dauer"
                  value={newTask.duration_days || 0}
                  onChange={(e) =>
                    setNewTask({
                      ...newTask,
                      duration_days: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
              </td>
              <td>
                <select
                  value={newTask.role || ''}
                  onChange={(e) =>
                    setNewTask({ ...newTask, role: e.target.value })
                  }
                >
                  <option value="">Wähle eine Rolle</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.fields?.name || role.id}
                    </option>
                  ))}
                </select>
              </td>
              <td className="actions-td">
                <button className="btn save" onClick={handleNewSave}>
                  💾
                </button>
                <button className="btn discard" onClick={handleNewCancel}>
                  ✖
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Button "Neue Aufgabe erstellen" */}
      {!newTask && (
        <button
          className="btn new-group"
          onClick={() =>
            setNewTask({
              name: '',
              clickup_tmpid: '',
              duration_days: 0, // Als Zahl initialisieren
              role: '',
            })
          }
        >
          + Neue Aufgabe
        </button>
      )}
    </>
  );
};

export default TasksTablePage;